import { NgIf } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { cloneDeep } from 'lodash';
import { Observable, forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { FORMLY_FIELD_TYPES, FORMLY_FIELD_VALIDATORS, FORMLY_FIELD_WRAPPERS } from '@app/constants';
import { DialogService, FeedbackService, LoadingService } from '@app/core/services';
import { PaymentCardTypeEnum } from '@app/enums/system-settings/payment-card-type.enum';
import { IAccountSearchModel } from '@app/interfaces/lsb/account-search';
import { BaseServerFilterEntity } from '@app/models';
import { PaymentCardModel } from '@app/models/system-settings/payment-card.model';
import { ErrorSummaryComponent } from '@app/shared/components/error-summary/error-summary.component';
import { FormMode } from '@app/shared/forms/components';
import { FormComponent } from '@app/shared/forms/components/form.component';
import { AccountService, AccountStructureService } from '@app/shared/services';
import { BankService } from '@app/shared/services/bank.service';
import { PurchaseCardSetupService } from '../services/purchase-card-setup.service';

@Component({
	selector: 'virtual-card-setup',
	templateUrl: './virtual-card-setup.component.html',
	styleUrls: ['./virtual-card-setup.component.less'],
	standalone: true,
	imports: [ErrorSummaryComponent, FormComponent, NgIf, MatButtonModule],
})
export class VirtualCardSetupComponent implements OnInit {
	errors: string[] = [];
	entity: PaymentCardModel = {
		Id: 0,
		TypeId: PaymentCardTypeEnum.VirtualCard,
	};

	originalEntity: PaymentCardModel;

	@Input() isReadOnly: boolean;

	fields: FormlyFieldConfig[] = [];

	showTestButton = false;

	options: FormlyFormOptions = {
		formState: {
			mode: FormMode.Edit,
			readOnly: false,
			flyout: false,
			selects: {
				banks: [],
			},
		},
	};

	public form = new UntypedFormGroup({});
	private questionMarkMask: string;

	constructor(
		private readonly loadingService: LoadingService,
		private readonly purchaseCardSetupService: PurchaseCardSetupService,
		private readonly bankService: BankService,
		private readonly accountService: AccountService,
		protected readonly dialogService: DialogService,
		protected readonly feedbackService: FeedbackService,
		private readonly accountStructureService: AccountStructureService
	) {}

	ngOnInit(): void {
		forkJoin([
			this.bankService.getBanks(false, true),
			this.accountService.searchAccounts({
				AccountNumber: '',
				Limit: 10000,
				OnlyActive: true,
			} as IAccountSearchModel),
			this.accountStructureService.getStructure(),
			this.purchaseCardSetupService.getCardByType(PaymentCardTypeEnum.VirtualCard),
		]).subscribe(([bankResult, accountResult, accountStructureResult, purchaseCardResult]) => {
			if (bankResult.banks) {
				this.options.formState.selects.banks = bankResult.banks.map((bank) => ({
					value: bank.Id,
					label: `${bank.DisplayName}`,
				}));
			}

			if (accountResult.accounts) {
				this.options.formState.selects.activeAccounts = accountResult.accounts.map((account) => ({
					value: account.Id,
					label: `${account.AccountNumber}: ${account.Description}`,
				}));
			}

			if (accountStructureResult.accountStructure) {
				this.questionMarkMask = (accountStructureResult?.accountStructure?.UnifiedAccountMask || '').replace(/0/g, '?');
			}

			this.loadingService.hide();
			if (purchaseCardResult.isValid && purchaseCardResult.data) {
				this.entity = this.createPaymentCardModel(purchaseCardResult.data);
				if (purchaseCardResult.data.BankId) {
					this.getAccountsByBankId(purchaseCardResult.data.BankId);
				}
			}
			this.originalEntity = cloneDeep(this.entity);

			this.setFields();

			this.showTestButton = true;
		});

		if (this.isReadOnly) {
			this.options.formState.mode = FormMode.View;
		}
	}

	public CanDeactivate(): boolean {
		return !this.form.dirty;
	}

	getAccountsByBankId(bankId: number): void {
		this.loadingService.skipNextRequest();
		this.accountService
			.searchAccounts({
				AccountNumber: '',
				Limit: 1000,
				BankId: bankId,
			} as IAccountSearchModel)
			.subscribe((cashAccounts) => {
				this.options.formState.selects.cashAccounts = cashAccounts.accounts.map((account) => ({
					value: account.Id,
					label: `${account.AccountNumber}: ${account.Description}`,
				}));
			});
	}

	cancelChanges(): void {
		this.dialogService
			.open({
				title: 'Unsaved Changes',
				message: 'There are unsaved changes.  Are you sure you want to cancel?',
				okText: 'Yes',
				cancelText: 'No',
			})
			.subscribe((result) => {
				if (result) {
					this.entity = cloneDeep(this.originalEntity);
					this.setFields();
					this.form.markAsPristine();
				}
			});
	}

	saveChanges(): void {
		this.errors = [];
		this.entity.VendorId = this.entity.VendorFilterEntity.Value;
		this.entity.DueFromOtherFundsAccountId = this.entity.DueFromOtherFundsAccountFilterEntity.Value;
		this.entity.CashAccountId = this.entity.CashAccountFilterEntity.Value;
		this.purchaseCardSetupService.createOrUpdate(this.entity).subscribe((result) => {
			if (result.isValid) {
				this.entity = this.createPaymentCardModel(result.data);
				this.feedbackService.success('updated', 'Virtual Card');
				this.setFields();
				this.form.markAsPristine();
				this.originalEntity = cloneDeep(this.entity);
			} else {
				this.errors = result.errors;
			}
		});
	}

	testConnection(): void {
		this.errors = [];
		this.purchaseCardSetupService
			.testConnection(this.entity.SftpUrl, this.entity.SftpUserName, this.entity.SftpPassword, this.entity.SftpPort, this.entity.SftpDirectory)
			.subscribe((result) => {
				if (result.Errors.length > 0) {
					this.errors = result.errors.join(',');
				} else {
					this.feedbackService.customSuccess('Connection Successful');
				}
			});
	}

	private setFields(): void {
		this.fields = [
			{
				fieldGroupClassName: 'row',
				fieldGroup: [
					{
						key: 'VendorFilterEntity',
						className: 'col-xs-8 col-md-6',
						type: FORMLY_FIELD_TYPES.autocomplete,
						templateOptions: {
							label: 'Vendor',
							required: true,
							filter: (value: string | any): Observable<any> => {
								this.loadingService.skipNextRequest();
								return this.filterAutocomplete(value);
							},
							filterDisplayValueFunc: (value: BaseServerFilterEntity): any => value.Label,
							filterValueFunc: (value: BaseServerFilterEntity): any => value.Value,
							dropCustomValues: true,
							maxLength: 50,
							selectedItem: this.entity.VendorFilterEntity?.Label,
						},
					},
				],
			},
			{
				fieldGroupClassName: 'row',
				wrappers: [FORMLY_FIELD_WRAPPERS.sectionSimpleTitle],
			},
			{
				fieldGroupClassName: 'row',
				fieldGroup: [
					{
						key: 'DueToOtherFundsAccountMask',
						className: 'col-xs-6 col-md-4',
						type: FORMLY_FIELD_TYPES.maskedInput,
						templateOptions: {
							label: 'Due To Other Funds Account Mask',
							required: true,
							showMaskTyped: false,
							dropSpecialCharacters: false,
							questionMarkMask: this.questionMarkMask,
							maxLength: this.questionMarkMask.length,
						},
					},
				],
			},
			{
				fieldGroupClassName: 'row',
				fieldGroup: [
					{
						key: 'DueFromOtherFundsAccountFilterEntity',
						className: 'col-xs-6 col-md-4',
						type: FORMLY_FIELD_TYPES.autocomplete,
						templateOptions: {
							label: 'Due from Other Funds Acct',
							required: true,
							filter: (value: string | any): Observable<any> => {
								this.loadingService.skipNextRequest();
								return this.filterAccounts(value, 'DueFromOtherFundsAccountFilterEntity');
							},
							filterDisplayValueFunc: (value: BaseServerFilterEntity): any => value.Label,
							filterValueFunc: (value: BaseServerFilterEntity): any => value.Value,
							dropCustomValues: true,
							maxLength: 50,
							selectedItem: this.entity.DueFromOtherFundsAccountFilterEntity?.Label,
						},
					},
				],
			},
			{
				fieldGroupClassName: 'row',
				fieldGroup: [
					{
						key: 'VccId1',
						className: 'col-xs-4 col-md-2',
						type: FORMLY_FIELD_TYPES.input,
						templateOptions: {
							label: 'Company Number 1',
							required: true,
							maxLength: 25,
						},
						validators: {
							validation: [FORMLY_FIELD_VALIDATORS.requiredNoWhitespace],
						},
					},
					{
						key: 'VccId2',
						className: 'col-xs-4 col-md-2',
						type: FORMLY_FIELD_TYPES.input,
						templateOptions: {
							label: 'Company Number 2',
							required: true,
							maxLength: 25,
						},
						validators: {
							validation: [FORMLY_FIELD_VALIDATORS.requiredNoWhitespace],
						},
					},
					{
						key: 'VccId3',
						className: 'col-xs-4 col-md-2',
						type: FORMLY_FIELD_TYPES.input,
						templateOptions: {
							label: 'Company Number 3',
							required: true,
							maxLength: 25,
						},
						validators: {
							validation: [FORMLY_FIELD_VALIDATORS.requiredNoWhitespace],
						},
					},
				],
			},
			{
				fieldGroupClassName: 'row',
				fieldGroup: [
					{
						key: 'BankId',
						className: 'col-xs-6 col-md-4',
						type: FORMLY_FIELD_TYPES.select,
						templateOptions: {
							label: 'Bank',
							required: true,
							multiple: false,
							change: (field: FormlyFieldConfig): void => {
								field.form.get('CashAccountFilterEntity').patchValue(null);
							},
						},
						expressionProperties: {
							'templateOptions.options': 'formState.selects.banks',
						},
					},
				],
			},
			{
				fieldGroupClassName: 'row',
				fieldGroup: [
					{
						key: 'CashAccountFilterEntity',
						className: 'col-xs-6 col-md-4',
						type: FORMLY_FIELD_TYPES.autocomplete,
						templateOptions: {
							label: 'Cash Account',
							required: true,
							filter: (value: string | any): Observable<any> => {
								this.loadingService.skipNextRequest();
								return this.filterAccounts(value, 'CashAccountFilterEntity');
							},
							filterDisplayValueFunc: (value: BaseServerFilterEntity): any => value.Label,
							filterValueFunc: (value: BaseServerFilterEntity): any => value.Value,
							dropCustomValues: true,
							maxLength: 50,
							selectedItem: this.entity.CashAccountFilterEntity?.Label,
						},
						expressionProperties: {
							'templateOptions.readonly': (model) => !model.BankId,
						},
					},
				],
			},
			{
				fieldGroupClassName: 'row',
				wrappers: [FORMLY_FIELD_WRAPPERS.customPanelWithTitle],
				className: 'col-xs-6 col-md-4',
				templateOptions: {
					section: 'sftpCriteria',
					label: 'SFTP Criteria',
					useSubHeading: true,
				},
				fieldGroup: [
					{
						key: 'SftpUrl',
						className: 'col-xs-12',
						type: FORMLY_FIELD_TYPES.input,
						templateOptions: {
							label: 'URL',
							required: true,
							maxLength: 50,
						},
						validators: {
							validation: [FORMLY_FIELD_VALIDATORS.requiredNoWhitespace],
						},
					},
					{
						key: 'SftpUserName',
						className: 'col-xs-12',
						type: FORMLY_FIELD_TYPES.input,
						templateOptions: {
							label: 'User Name',
							required: true,
							maxLength: 50,
						},
						validators: {
							validation: [FORMLY_FIELD_VALIDATORS.requiredNoWhitespace],
						},
					},
					{
						key: 'SftpPassword',
						className: 'col-xs-12',
						type: FORMLY_FIELD_TYPES.input,
						templateOptions: {
							type: 'password',
							label: 'Password',
							required: true,
							maxLength: 50,
							attributes: {
								autocomplete: 'new-password',
							},
						},
						validators: {
							validation: [FORMLY_FIELD_VALIDATORS.requiredNoWhitespace],
						},
					},
					{
						key: 'SftpDirectory',
						className: 'col-xs-12',
						type: FORMLY_FIELD_TYPES.input,
						templateOptions: {
							label: 'Directory',
							required: true,
							maxLength: 50,
						},
						validators: {
							validation: [FORMLY_FIELD_VALIDATORS.requiredNoWhitespace],
						},
					},
					{
						key: 'SftpPort',
						className: 'col-xs-6',
						type: FORMLY_FIELD_TYPES.input,
						templateOptions: {
							label: 'Port',
							required: true,
							maxLength: 50,
						},
						validators: {
							validation: [FORMLY_FIELD_VALIDATORS.requiredNoWhitespace],
						},
					},
				],
			},
		];
	}

	private filterAutocomplete(value: string): Observable<any> {
		let filterValue = this.entity.VendorFilterEntity?.Label;
		if (!filterValue) {
			filterValue = value;
		}
		return this.purchaseCardSetupService.searchVendorsForFilter(filterValue).pipe(
			map((data) =>
				data.map((item) => {
					const label = `${item.ShortName} - ${item.VendorName}`;
					return new BaseServerFilterEntity(item.Id, label, 'vendorId');
				})
			)
		);
	}

	private filterAccounts(value: string, field: string): Observable<any> {
		let filterValue = this.entity[field]?.Label;
		if (!filterValue) {
			filterValue = value;
		}
		const bankId = field === 'CashAccountFilterEntity' ? this.entity.BankId : null;
		return this.accountService
			.searchAccounts({
				AccountNumber: filterValue,
				Limit: 10,
				AccountTypeIds: [],
				Filter: false,
				OnlyActive: true,
				BankId: bankId,
			} as IAccountSearchModel)
			.pipe(
				map((data) =>
					data.accounts.map((item) => {
						const label = `${item.AccountNumber}: ${item.Description}`;
						return new BaseServerFilterEntity(item.Id, label, 'accountId');
					})
				)
			);
	}

	private createPaymentCardModel(pcard: PaymentCardModel) {
		const model = {
			Id: pcard.Id,
			Vendor: pcard.Vendor,
			VendorId: pcard.VendorId,
			VendorFilterEntity: new BaseServerFilterEntity(
				pcard.Vendor.Id,
				pcard.Vendor ? `${pcard.Vendor.ShortName} - ${pcard.Vendor.VendorName}` : '',
				'vendorId'
			),
			TypeId: pcard.TypeId,
			DueToOtherFundsAccountMask: pcard.DueToOtherFundsAccountMask,
			DueFromOtherFundsAccountId: pcard.DueFromOtherFundsAccountId,
			DueFromOtherFundsAccountFilterEntity: new BaseServerFilterEntity(
				pcard.DueFromOtherFundsAccountId,
				pcard.DueFromOtherFundsAccount
					? `${pcard.DueFromOtherFundsAccount.AccountNumber}: ${pcard.DueFromOtherFundsAccount.Description}`
					: '',
				'accountId'
			),
			CompanyNumber: pcard.CompanyNumber,
			BankId: pcard.BankId,
			CashAccountId: pcard.CashAccountId,
			CashAccountFilterEntity: new BaseServerFilterEntity(
				pcard.CashAccountId,
				pcard.CashAccount ? `${pcard.CashAccount.AccountNumber}: ${pcard.CashAccount.Description}` : '',
				'accountId'
			),
			SftpUrl: pcard.SftpUrl,
			SftpUserName: pcard.SftpUserName,
			SftpPassword: pcard.SftpPassword,
			SftpDirectory: pcard.SftpDirectory,
			SftpPort: pcard.SftpPort,

			VccId1: pcard.VccId1,
			VccId2: pcard.VccId2,
			VccId3: pcard.VccId3,
		} as PaymentCardModel;

		return model;
	}
}
